"use client";

import { c as _c } from "react/compiler-runtime";
import type { AudienceMegaMenuHeaderRuntimePayload, MegaMenuHeaderRuntimePayload } from "@multi-tenant-cms/types/menu";
import { useCallback, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import styles from "./audience-mega-menu-header.module.css";
import { AUDIENCE_PREFERENCE_COOKIE, audienceIdFromCookie, audienceIdFromPath, defaultAudienceId, writeAudiencePreferenceCookie } from "./audienceMegaMenuPreference";
import { MegaMenuHeader } from "./MegaMenuHeader";
import { stashOpenMegaTabLabel } from "./megaMenuOpenTabRestore";
export { AUDIENCE_PREFERENCE_COOKIE, audienceIdFromCookie, audienceIdFromPath, defaultAudienceId, initialAudienceId } from "./audienceMegaMenuPreference";
const STORAGE_KEY = AUDIENCE_PREFERENCE_COOKIE;
function megaPayloadForAudience(payload: AudienceMegaMenuHeaderRuntimePayload, audienceId: string): MegaMenuHeaderRuntimePayload {
  const slot = payload.audiences.find(a => a.id === audienceId) ?? payload.audiences[0];
  return {
    logoMediaId: payload.logoMediaId,
    logoAlt: payload.logoAlt,
    logoHref: payload.logoHref,
    logoUrl: payload.logoUrl,
    logoWidth: payload.logoWidth,
    logoHeight: payload.logoHeight,
    logoHeightPx: payload.logoHeightPx,
    ...(payload.logoOpenVariant == null ? {} : {
      logoOpenVariant: payload.logoOpenVariant
    }),
    ...(payload.logoOpenUrl == null ? {} : {
      logoOpenUrl: payload.logoOpenUrl
    }),
    menuId: slot.menuId,
    menuName: slot.menuName,
    roots: slot.roots,
    contact: payload.contact,
    cta: payload.cta,
    languageSwitcher: payload.languageSwitcher,
    promos: slot.promos,
    footerRows: slot.footerRows ?? {},
    design: payload.design,
    closeDelayMs: payload.closeDelayMs,
    ...(payload.sticky === true ? {
      sticky: true as const,
      ...(payload.stickyMode === "smart" ? {
        stickyMode: "smart" as const
      } : {})
    } : {})
  };
}
function readStoredAudienceId(payload: AudienceMegaMenuHeaderRuntimePayload): string | null {
  if (!payload.persistPreference) {
    return null;
  }
  const validIds = new Set(payload.audiences.map(a => a.id));
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && validIds.has(stored)) {
      return stored;
    }
  } catch {
    // ignore quota / private mode
  }
  return null;
}
const storageListeners = new Set<() => void>();
function subscribeAudienceStorage(onStoreChange: () => void): () => void {
  storageListeners.add(onStoreChange);
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY || e.key === null) {
      onStoreChange();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    storageListeners.delete(onStoreChange);
    window.removeEventListener("storage", onStorage);
  };
}
function notifyAudienceStorageListeners(): void {
  for (const listener of storageListeners) {
    listener();
  }
}

/** Server / hydration snapshot — must not read localStorage. */
function getStoredAudienceServerSnapshot(): string | null {
  return null;
}
function AudienceToggle(t0) {
  const $ = _c(24);
  const {
    payload,
    audienceId,
    onSelect,
    variant,
    viewport: t1
  } = t0;
  const viewport = t1 === undefined ? null : t1;
  let t2;
  if ($[0] !== payload.tipLabel) {
    t2 = payload.tipLabel?.trim();
    $[0] = payload.tipLabel;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const tip = t2;
  const t3 = variant === "drawer" ? styles.drawerToggleWrap : styles.barToggleWrap;
  let t4;
  if ($[2] !== viewport) {
    t4 = viewport == null ? {} : {
      "data-viewport": viewport
    };
    $[2] = viewport;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== tip || $[5] !== variant) {
    t5 = tip ? <span className={variant === "drawer" ? styles.drawerTip : styles.tip}>{tip}</span> : null;
    $[4] = tip;
    $[5] = variant;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = variant === "drawer" ? styles.drawerSwitch : styles.switch;
  let t7;
  if ($[7] !== audienceId || $[8] !== onSelect || $[9] !== payload.audiences || $[10] !== variant) {
    let t8;
    if ($[12] !== audienceId || $[13] !== onSelect || $[14] !== variant) {
      t8 = slot => {
        const on = slot.id === audienceId;
        return <button aria-pressed={on ? "true" : "false"} className={`${styles.switchBtn} ${on ? styles.switchBtnOn : ""} ${variant === "drawer" ? styles.drawerSwitchBtn : ""}`} key={slot.id} onClick={() => onSelect(slot.id)} type="button">{slot.label}</button>;
      };
      $[12] = audienceId;
      $[13] = onSelect;
      $[14] = variant;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    t7 = payload.audiences.map(t8);
    $[7] = audienceId;
    $[8] = onSelect;
    $[9] = payload.audiences;
    $[10] = variant;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = <div aria-label="Choose audience" className={t6} role="group">{t7}</div>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t3 || $[20] !== t4 || $[21] !== t5 || $[22] !== t8) {
    t9 = <div className={t3} {...t4}>{t5}{t8}</div>;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}
export function AudienceMegaMenuHeader(t0) {
  const $ = _c(48);
  const {
    payload,
    disableNavigation: t1,
    currentPathname: t2,
    preferredAudienceId: t3,
    forcedViewport: t4
  } = t0;
  const disableNavigation = t1 === undefined ? false : t1;
  const currentPathname = t2 === undefined ? null : t2;
  const preferredAudienceId = t3 === undefined ? null : t3;
  const forcedViewport = t4 === undefined ? null : t4;
  let t5;
  if ($[0] !== currentPathname || $[1] !== payload) {
    t5 = audienceIdFromPath(payload, currentPathname);
    $[0] = currentPathname;
    $[1] = payload;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const pathAudienceId = t5;
  let t6;
  if ($[3] !== payload || $[4] !== preferredAudienceId) {
    t6 = audienceIdFromCookie(payload, preferredAudienceId);
    $[3] = payload;
    $[4] = preferredAudienceId;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const cookieAudienceId = t6;
  let t7;
  if ($[6] !== payload) {
    t7 = () => readStoredAudienceId(payload);
    $[6] = payload;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const storedAudienceId = useSyncExternalStore(subscribeAudienceStorage, t7, getStoredAudienceServerSnapshot);
  const [manualOverride, setManualOverride] = useState(null);
  const openMegaLabelRef = useRef(null);
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setManualOverride(null);
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== currentPathname) {
    t9 = [currentPathname];
    $[9] = currentPathname;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  useLayoutEffect(t8, t9);
  let t10;
  let t11;
  if ($[11] !== cookieAudienceId || $[12] !== payload.persistPreference || $[13] !== storedAudienceId) {
    t10 = () => {
      if (!(payload.persistPreference && storedAudienceId && !cookieAudienceId)) {
        return;
      }
      writeAudiencePreferenceCookie(storedAudienceId);
    };
    t11 = [payload.persistPreference, storedAudienceId, cookieAudienceId];
    $[11] = cookieAudienceId;
    $[12] = payload.persistPreference;
    $[13] = storedAudienceId;
    $[14] = t10;
    $[15] = t11;
  } else {
    t10 = $[14];
    t11 = $[15];
  }
  useLayoutEffect(t10, t11);
  let t12;
  if ($[16] !== cookieAudienceId || $[17] !== manualOverride || $[18] !== pathAudienceId || $[19] !== payload || $[20] !== storedAudienceId) {
    t12 = manualOverride ?? pathAudienceId ?? cookieAudienceId ?? storedAudienceId ?? defaultAudienceId(payload);
    $[16] = cookieAudienceId;
    $[17] = manualOverride;
    $[18] = pathAudienceId;
    $[19] = payload;
    $[20] = storedAudienceId;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const audienceId = t12;
  let t13;
  if ($[22] !== audienceId || $[23] !== payload) {
    t13 = megaPayloadForAudience(payload, audienceId);
    $[22] = audienceId;
    $[23] = payload;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const megaPayload = t13;
  let t14;
  if ($[25] !== disableNavigation || $[26] !== payload.audiences || $[27] !== payload.navigateOnToggle || $[28] !== payload.persistPreference) {
    t14 = nextId => {
      if (!payload.audiences.some(a => a.id === nextId)) {
        return;
      }
      setManualOverride(nextId);
      if (payload.persistPreference) {
        try {
          window.localStorage.setItem(STORAGE_KEY, nextId);
          notifyAudienceStorageListeners();
        } catch {}
        writeAudiencePreferenceCookie(nextId);
      }
      const slot = payload.audiences.find(a_0 => a_0.id === nextId);
      const hub = slot?.hubUrl?.trim() ?? "";
      const willNavigate = !(disableNavigation || !payload.navigateOnToggle || !hub || hub === "#");
      const openLabel = openMegaLabelRef.current?.trim();
      if (willNavigate && openLabel) {
        stashOpenMegaTabLabel(openLabel);
      }
      if (willNavigate) {
        window.location.assign(hub);
      }
    };
    $[25] = disableNavigation;
    $[26] = payload.audiences;
    $[27] = payload.navigateOnToggle;
    $[28] = payload.persistPreference;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const setAudience = t14;
  let t15;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = tab => {
      openMegaLabelRef.current = tab?.label.trim() || null;
    };
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const onActiveMegaTabChange = t15;
  const builderViewport = forcedViewport === "mobile" || forcedViewport === "tablet" || forcedViewport === "desktop" ? forcedViewport : null;
  let t16;
  if ($[31] !== audienceId || $[32] !== builderViewport || $[33] !== payload || $[34] !== setAudience) {
    t16 = <AudienceToggle audienceId={audienceId} onSelect={setAudience} payload={payload} variant="bar" viewport={builderViewport} />;
    $[31] = audienceId;
    $[32] = builderViewport;
    $[33] = payload;
    $[34] = setAudience;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== audienceId || $[37] !== builderViewport || $[38] !== payload || $[39] !== setAudience) {
    t17 = <AudienceToggle audienceId={audienceId} onSelect={setAudience} payload={payload} variant="drawer" viewport={builderViewport} />;
    $[36] = audienceId;
    $[37] = builderViewport;
    $[38] = payload;
    $[39] = setAudience;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== currentPathname || $[42] !== disableNavigation || $[43] !== forcedViewport || $[44] !== megaPayload || $[45] !== t16 || $[46] !== t17) {
    t18 = <MegaMenuHeader afterLogo={t16} currentPathname={currentPathname} drawerLead={t17} forcedViewport={forcedViewport} onActiveMegaTabChange={onActiveMegaTabChange} payload={megaPayload} previewMode={disableNavigation} />;
    $[41] = currentPathname;
    $[42] = disableNavigation;
    $[43] = forcedViewport;
    $[44] = megaPayload;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  return t18;
}